import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  TextField, Switch, FormControlLabel, Autocomplete, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { notificationRuleService } from '../services/api';
import api from '../services/api';

const EMPTY = { action_type: '', notify_roles: [], notify_staff: [], is_active: true, description: '' };

export default function NotificationRulesPage() {
  const [rules, setRules] = useState([]);
  const [actionChoices, setActionChoices] = useState([]);
  const [roles, setRoles] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, ro, st] = await Promise.all([
        notificationRuleService.getAll(),
        notificationRuleService.getActionChoices(),
        api.get('/roles/', { params: { mini: 1 } }),
        api.get('/staff-mini/'),
      ]);
      setRules(r.data.results || r.data);
      setActionChoices(c.data);
      setRoles(ro.data.results || ro.data);
      setStaffList(st.data.results || st.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (rule) => {
    setEditing(rule.id);
    setForm({
      action_type: rule.action_type,
      notify_roles: rule.notify_roles,
      notify_staff: rule.notify_staff,
      is_active: rule.is_active,
      description: rule.description,
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form };
    if (editing) {
      await notificationRuleService.update(editing, payload);
    } else {
      await notificationRuleService.create(payload);
    }
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Видалити правило?')) return;
    await notificationRuleService.remove(id);
    load();
  };

  const toggleActive = async (rule) => {
    await notificationRuleService.update(rule.id, { is_active: !rule.is_active });
    load();
  };

  const actionLabel = (val) => {
    const found = actionChoices.find(([v]) => v === val);
    return found ? found[1] : val;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <NotificationsIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>Правила сповіщень</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ ml: 'auto' }}>
          Додати правило
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Тут налаштовується: при якій події — хто отримує сповіщення в системі.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Подія</TableCell>
                <TableCell>Ролі що отримують</TableCell>
                <TableCell>Конкретні співробітники</TableCell>
                <TableCell>Опис</TableCell>
                <TableCell align="center">Активно</TableCell>
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Правил ще немає. Додайте перше правило.
                  </TableCell>
                </TableRow>
              )}
              {rules.map(rule => (
                <TableRow key={rule.id} hover sx={{ opacity: rule.is_active ? 1 : 0.5 }}>
                  <TableCell>
                    <Chip label={actionLabel(rule.action_type)} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(rule.notify_roles_data || []).map(r => (
                        <Chip key={r.id} label={r.name} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(rule.notify_staff_data || []).map(s => (
                        <Chip key={s.id} label={s.name} size="small" variant="outlined" color="secondary" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{rule.description}</TableCell>
                  <TableCell align="center">
                    <Switch size="small" checked={rule.is_active} onChange={() => toggleActive(rule)} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Редагувати">
                      <IconButton size="small" onClick={() => openEdit(rule)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Видалити">
                      <IconButton size="small" color="error" onClick={() => remove(rule.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редагувати правило' : 'Нове правило сповіщення'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Подія *</InputLabel>
            <Select value={form.action_type} label="Подія *"
              onChange={e => setForm(f => ({ ...f, action_type: e.target.value }))}>
              {actionChoices.map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            multiple
            options={roles}
            getOptionLabel={o => o.name}
            value={roles.filter(r => (form.notify_roles || []).includes(r.id))}
            onChange={(_, val) => setForm(f => ({ ...f, notify_roles: val.map(v => v.id) }))}
            renderTags={(val, getProps) => val.map((opt, i) => (
              <Chip label={opt.name} size="small" {...getProps({ index: i })} />
            ))}
            renderInput={params => <TextField {...params} label="Ролі що отримують сповіщення" />}
          />

          <Autocomplete
            multiple
            options={staffList}
            getOptionLabel={o => o.full_name || o.username}
            value={staffList.filter(s => (form.notify_staff || []).includes(s.id))}
            onChange={(_, val) => setForm(f => ({ ...f, notify_staff: val.map(v => v.id) }))}
            renderTags={(val, getProps) => val.map((opt, i) => (
              <Chip label={opt.full_name || opt.username} size="small" {...getProps({ index: i })} />
            ))}
            renderInput={params => <TextField {...params} label="Конкретні співробітники" />}
          />

          <TextField
            label="Опис правила"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            multiline rows={2}
          />

          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />}
            label="Активне правило"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={save} disabled={!form.action_type}>Зберегти</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
